import { useCallback, useEffect, useRef, useState } from 'react';

export const MAX_RECORD_SECONDS = 600;
const MIME_ORDER = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/mp4'];

const extFor = (mime) => (mime.includes('ogg') ? 'ogg' : mime.includes('mp4') ? 'm4a' : 'webm');

/**
 * Records a voice note with MediaRecorder. `start()` resolves true once recording began (false after
 * a toast-worthy failure reported through onError). `stop()` resolves the finished File (voice-note.<ext>)
 * and `cancel()` throws the take away. Tracks are always released on stop/cancel/unmount.
 * @param {{onError: (message: string) => void, onAutoStop?: (file: File | null) => void}} opts
 */
const useVoiceRecorder = ({ onError, onAutoStop }) => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const startedAtRef = useRef(0);
  const startingRef = useRef(false);
  const onErrorRef = useRef(onError);
  const onAutoStopRef = useRef(onAutoStop);
  onErrorRef.current = onError;
  onAutoStopRef.current = onAutoStop;

  const release = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Resolves the File (or null when there is nothing) and fully tears down.
  const finish = useCallback(
    (discard) =>
      new Promise((resolve) => {
        const recorder = recorderRef.current;
        recorderRef.current = null;
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = null;
        if (mountedRef.current) {
          setRecording(false);
          setSeconds(0);
        }
        if (!recorder) {
          release();
          return resolve(null);
        }
        const build = () => {
          release(); // after the recorder flushed, so the tail of the take is not clipped
          let type = (recorder.mimeType || 'audio/webm').split(';')[0];
          if (type === 'video/webm') type = 'audio/webm'; // audio-only take reported as video by some browsers
          const chunks = chunksRef.current;
          chunksRef.current = [];
          if (discard || chunks.length === 0) return resolve(null);
          resolve(new File(chunks, `voice-note.${extFor(type)}`, { type }));
        };
        if (recorder.state === 'inactive') return build();
        recorder.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
        recorder.onstop = build;
        try {
          recorder.stop();
        } catch {
          build();
        }
      }),
    [release],
  );

  const start = useCallback(async () => {
    if (recorderRef.current || startingRef.current) return false;
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      onErrorRef.current('Voice notes are not supported in this browser.');
      return false;
    }
    let stream;
    startingRef.current = true;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      startingRef.current = false;
      onErrorRef.current('Microphone access was blocked. Allow the microphone in your browser settings to record.');
      return false;
    }
    startingRef.current = false;
    if (!mountedRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return false;
    }
    const mimeType = MIME_ORDER.find((m) => window.MediaRecorder.isTypeSupported?.(m));
    let recorder;
    try {
      recorder = mimeType ? new window.MediaRecorder(stream, { mimeType }) : new window.MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      onErrorRef.current('Voice notes are not supported in this browser.');
      return false;
    }
    streamRef.current = stream;
    recorderRef.current = recorder;
    chunksRef.current = [];
    recorder.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
    recorder.start();
    startedAtRef.current = Date.now();
    setSeconds(0);
    setRecording(true);
    // Wall clock, not tick counting: hidden tabs throttle timers.
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
      if (mountedRef.current) setSeconds(Math.min(elapsed, MAX_RECORD_SECONDS));
      if (elapsed >= MAX_RECORD_SECONDS) {
        finish(false).then((file) => onAutoStopRef.current?.(file));
      }
    }, 250);
    return true;
  }, [finish]);

  const stop = useCallback(() => finish(false), [finish]);
  const cancel = useCallback(() => finish(true), [finish]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const recorder = recorderRef.current;
      recorderRef.current = null;
      if (recorder) {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        try {
          if (recorder.state !== 'inactive') recorder.stop();
        } catch {
          /* already stopped */
        }
      }
      release();
    };
  }, [release]);

  return { recording, seconds, start, stop, cancel };
};

export default useVoiceRecorder;
